
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Contact
 *
 * A person or company in the firm's address book. Contacts serve as clients on cases and leads, as case participants (opposing parties, witnesses, experts, etc.), and as the underlying record for firm users. A contact may be an individual (default) or a company (isCompany=true); individuals can be linked to a company via companyId. Contacts sync with the payment processor and may be granted a client portal login.
 */
class Contact extends Base implements EntityType {
    contactId: uuid;
    prefix?: string | null;
    last?: string | null;
    first?: string | null;
    middle?: string | null;
    suffix?: string | null;
    nick?: string | null;
    aliases?: string | null;
    billingName?: string | null;
    title?: string | null;
    companyId?: uuid | null;
    isCompany: boolean;
    tags: string[];
    portalUser: boolean;
    processorContactId?: string | null;
    lf?: string | null;
    lfm?: string | null;
    fl?: string | null;
    fml?: string | null;
    uniqueLFM?: string | null;
    uniqueLFMComputed?: number | null;
    uniqueFML?: string | null;
    uniqueFMLComputed?: number | null;

    /**
     * Create a new Contact, with an (optional) JSON object or Contact class
     *
     * @param {Object|Contact} doc - Can be either a simple JSON object or a Contact class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Contact | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            contactId: genUUID(),
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'contact';
        // @ts-ignore
        this.contactId = doc._contactId || doc.contactId || doc.contactid || genUUID();
        // @ts-ignore
        this.prefix = doc._prefix || doc.prefix || null;
        // @ts-ignore
        this.last = doc._last || doc.last || null;
        // @ts-ignore
        this.first = doc._first || doc.first || null;
        // @ts-ignore
        this.middle = doc._middle || doc.middle || null;
        // @ts-ignore
        this.suffix = doc._suffix || doc.suffix || null;
        // @ts-ignore
        this.nick = doc._nick || doc.nick || null;
        // @ts-ignore
        this.aliases = doc._aliases || doc.aliases || null;
        // @ts-ignore
        this.billingName = doc._billingName || doc.billingName || doc.billingname || null;
        // @ts-ignore
        this.title = doc._title || doc.title || null;
        // @ts-ignore
        this.companyId = doc._companyId || doc.companyId || doc.companyid || null;
        // @ts-ignore
        this.isCompany = util.defaultBoolean([doc.isCompany, doc._isCompany, doc.iscompany, false]);
        // @ts-ignore
        this.tags = doc._tags || doc.tags || [];
        // @ts-ignore
        this.portalUser = util.defaultBoolean([doc.portalUser, doc._portalUser, doc.portaluser, false]);
        // @ts-ignore
        this.processorContactId = doc._processorContactId || doc.processorContactId || doc.processorcontactid || null;
        // @ts-ignore
        this.lf = doc._lf || doc.lf || null;
        // @ts-ignore
        this.lfm = doc._lfm || doc.lfm || null;
        // @ts-ignore
        this.fl = doc._fl || doc.fl || null;
        // @ts-ignore
        this.fml = doc._fml || doc.fml || null;
        // @ts-ignore
        this.uniqueLFM = doc._uniqueLFM || doc.uniqueLFM || doc.uniquelfm || null;
        // @ts-ignore
        this.uniqueLFMComputed = util.coalesce(util.parseFloat(doc._uniqueLFMComputed), util.parseFloat(doc.uniqueLFMComputed), util.parseFloat(doc.uniquelfmcomputed), null);
        // @ts-ignore
        this.uniqueFML = doc._uniqueFML || doc.uniqueFML || doc.uniquefml || null;
        // @ts-ignore
        this.uniqueFMLComputed = util.coalesce(util.parseFloat(doc._uniqueFMLComputed), util.parseFloat(doc.uniqueFMLComputed), util.parseFloat(doc.uniquefmlcomputed), null);

    }
    /**
     * @returns {uuid} This is a generic pointer to the contactId across all entity types
     */
    get id(): uuid {
        return this.contactId;
    }


    /**
     * Add a tag to contact.tags
     *
     * @param {string} tag - The Tag to add
     * @returns string - The tag with any defaulted fields set
     */
    addTag(tag: string) : string {
        if (!tag) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.tags) this.tags = [];
        this.tags.push(tag);

        return tag;
    }

    /**
     * Update a tag from contact
     *
     * @param {string} tag - The Tag to remove
     */
    updateTag(tag: string) {
        if (!this.tags) this.tags = [];
        
        // Find the matching object by tag.tagId
        const index = this.tags.findIndex(item => item === tag);
        if (index !== -1) {
            this.tags[index] = tag;
        }
        else this.addTag(tag);
    }

    /**
     * Remove a tag from contact
     *
     * @param {string} tag - The Tag to remove
     */
    removeTag(tag: string) {
        if (!this.tags) return;
        
        // Find the matching object by tag.tagId
        let foundItems = this.tags?.filter(item => item === tag);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.tags = this.tags.filter(item => !(item === tag));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonTags() {
        this.tags = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for contact"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.contactId;
    }
    
    /**
     * How should this item be displayed in a list?
     */
    displayFn() {
        return `${this.last}, ${this.first} ${this.middle}`;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["tags"];
    }

    /**
     * Default entries are Outlaw-supplied Contact entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Contact[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'contact',
            table: 'CONTACT',
            comment: `A person or company in the firm's address book. Contacts serve as clients on cases and leads, as case participants (opposing parties, witnesses, experts, etc.), and as the underlying record for firm users. A contact may be an individual (default) or a company (isCompany=true); individuals can be linked to a company via companyId. Contacts sync with the payment processor and may be granted a client portal login.`,
            fields: {
                contactId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for contact'
                },
                prefix: {
                    type: 'string',
                    maxLength: 32,
                    comment: 'Mr., Mrs., etc'
                },
                last: {
                    type: 'string',
                    comment: 'Last name(s)'
                },
                first: {
                    type: 'string',
                    comment: 'First name'
                },
                middle: {
                    type: 'string',
                    comment: 'Middle name(s)'
                },
                suffix: {
                    type: 'string',
                    maxLength: 32,
                    comment: 'Jr., Sr., III, etc'
                },
                nick: {
                    type: 'string',
                    comment: 'Nickname'
                },
                aliases: {
                    type: 'string',
                    comment: 'Aliases (as opposed to nicknames, could use either)'
                },
                billingName: {
                    type: 'string',
                    comment: 'The name to use on invoices (fully formatted)'
                },
                title: {
                    type: 'string',
                    comment: 'Business title'
                },
                // For now, we are just storing the company name that the person works for as the uuid of another contact
                // where isCompany is true.
                companyId: {
                    type: 'uuid',
                    entityType: 'contact',
                    comment: 'ID of the company (contact) that this person works for'
                },
                isCompany: {
                    type: 'boolean',
                    comment: 'Is this a company?'
                },
                tags: {
                    type: 'string[]',
                    comment: 'Any tags, for quick searches. Need a text index here'
                },
                portalUser: {
                    type: 'boolean',
                    comment: 'Does this user have a portal account with auth0?'
                },
                processorContactId: {
                    type: 'string',
                    entityType: 'lawpay',
                    comment: 'Related contact ID from the processor'
                },
                /* Pre-computed (transient) Fields */
                lf: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'Last First no middle'
                },
                lfm: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'Last First Middle'
                },
                fl: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'First Last'
                },
                fml: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'First Middle Last'
                },
                uniqueLFM: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'Unique name in LFM format'
                },
                uniqueLFMComputed: {
                    type: 'float',
                    transient: true,
                    computed: true,
                    comment: 'addedOn for computed LFM'
                },
                uniqueFML: {
                    type: 'string',
                    transient: true,
                    computed: true,
                    comment: 'Unique name in FML format'
                },
                uniqueFMLComputed: {
                    type: 'float',
                    transient: true,
                    computed: true,
                    comment: 'addedOn for computed FML'
                },
            },
            displayFn: "`${this.last}, ${this.first} ${this.middle}`",
            externalRefs: [
                'document', 'comment', 'identification', 'event', 'participant'
            ],
            sortOrder: [{
                    position: 1,
                    fieldName: 'last',
                    asc: true
                },
                {
                    position: 2,
                    fieldName: 'first',
                    asc: true
                },
                {
                    position: 3,
                    fieldName: 'middle',
                    asc: true
                }
            ],
            indexes: [{
                name: 'notDeleted',
                fn: c => c.status !== 'D'
            }],
            hooks: [{
                    when: 'before',
                    appliesTo: 'save',
                    method: 'precomputeContact',
                    async: false
                },
                {
                    when: 'after',
                    appliesTo: 'find',
                    method: 'precomputeContact',
                    async: false
                },
                {
                    when: 'after',
                    appliesTo: 'save',
                    method: 'updatePaymentProcessor',
                    async: true
                }
            ],
            maps: [{
                name: 'companyId',
                keysFn: t => t.companyId,
                presentFn: (t, k) => t.companyId === k
            }]
        });
    }

}
export default Contact;
